import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GettingStartedProgressDto, OnboardingBrand } from './useGettingStartedOnboarding';
import { QueryKey } from '@/Config/api/queryKey';
import { SocketKey } from '@/Config/api/socketKey';
import { usePreview } from '@/Domain/components/PreviewProvider';
import { useSocket } from '@/Domain/components/SocketProvider';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { parseHost } from '@/Domain/helper/scrapeUrlClassifier';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  setSiteMapPending,
  SITEMAP_CONSUMED_KEY,
} from '@/UI/KnowledgeBase/SiteScrapeReview/siteMapFlags';
import { getCurrentScenarioId } from '@/context';

// Idle give-up window, re-armed on every backend progress event (so a reconnect or a streaming run resets the clock). Must stay above the backend TASK budget, not just the scrape budget: onboard_from_url_background is `@background_decorator(timeout=120)`, and both the scrape (`url_type_detected`→`article_created`) and the LLM settings call (`settings_generating`→`setting_applied`, `timeout=300s`) are single silent phases with no re-arming event, so a within-budget run can legitimately go quiet for most of those 120s. 150s keeps the original proven floor above that budget so a healthy run is never false-flagged; genuinely-sooner detection needs backend heartbeats (tracked follow-up).
export const IDLE_TIMEOUT_MS = 150_000;

/**
 * Refresh-resume signal: when the cached progress row shows
 * `knowledge_base_step_completed=true` and `automatic_flow_completed=false`,
 * the backend scrape completed in a previous session and the user was
 * somewhere in the post-step wizard (summary / external_apps / deployment)
 * when they reloaded. The hook initializes to `completed` in that case so
 * they resume inside the wizard instead of seeing the URL form again.
 *
 * The `knowledge_base_step_completed` flag is co-opted here because in the
 * automatic flow the scrape IS the knowledge-base setup — the user hands us
 * a URL, we ingest it, and the agent is configured off it. That makes the
 * existing flag semantically accurate for the automatic path without needing
 * a new column.
 */
function isAutomaticSetupComplete(progress: GettingStartedProgressDto | null | undefined): boolean {
  if (!progress) return false;
  return progress.automatic_source_type === 'description'
    ? progress.identity_step_completed
    : progress.knowledge_base_step_completed;
}

function hasCurrentRunCompleted(
  progress: GettingStartedProgressDto | null | undefined,
  sourceType: AutoOnboardingSourceType | null,
): boolean {
  if (sourceType === 'description') {
    return progress?.automatic_source_type === 'description' && progress.identity_step_completed;
  }
  return Boolean(progress?.knowledge_base_step_completed);
}

function isAutomaticResumeable(progress: GettingStartedProgressDto | null | undefined): boolean {
  return isAutomaticSetupComplete(progress) && !progress?.automatic_flow_completed;
}

// Code points, not UTF-16 units, so the count the user is measured against is
// the one the input's counter shows and the one the server's limits enforce.
export const descriptionLength = (description: string): number => Array.from(description).length;

// Falls back to a substring on unparseable input so `url_host` stays non-null
// in PostHog (real events were landing with null and breaking funnel joins).
export function extractUrlHost(url: string): string | undefined {
  if (!url || !url.trim()) return undefined;
  const parsedHost = parseHost(url);
  if (parsedHost) return parsedHost;
  const fallback = url
    .replace(/^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//, '')
    .split(/[/?#]/, 1)[0]
    .trim()
    .toLowerCase();
  return fallback || '-invalid-';
}

// Django Ninja serializes backend failures as `{ errors: { root: [{ message, code }] } }`
// (see `ErrorResponse.from_message` in `api/errors/models.py`). Pull out that
// message first so the user sees e.g. the 429 rate-limit copy; fall back to
// axios's own `error.message` ("Network Error", "Request failed with status
// code 500") for non-API failures.
interface ApiErrorResponse {
  errors?: {
    root?: { message: string; code?: string }[];
  };
}

// Coarse-grained error classes attached to `getting_started_automatic_failed`
// for filtering. `network` is the important one — it specifically captures
// the case where the POST never reached our backend (axios `Network Error`
// — DNS, CORS, dropped connection, gateway/edge timeout). Without this
// classification, a network-layer failure looks identical in PostHog to a
// 5xx returned by our own service, even though the diagnostic path is
// totally different. Internal-only — the UI doesn't read it.
type AutoOnboardingErrorClass =
  | 'network'
  | 'http_4xx'
  | 'http_5xx'
  | 'unknown'
  | 'backend'
  // the WS handler itself threw
  | 'client';

// User-facing error categories. Drives the localized message shown in the
// error view. Orthogonal to `error_class` (WHERE the failure happened) — the
// category answers WHY, from the user's perspective. Backend emits these for
// background-task failures; frontend derives them for network/HTTP failures.
export type AutoOnboardingErrorCategory =
  | 'scrape_failed' // URL couldn't be read (invalid, private, blocking automation)
  | 'access_blocked' // all scraping engines failed (HTTP 500), usually bot protection; retry is futile
  | 'timeout' // backend job exceeded its time budget
  | 'dns_failure' // hostname did not resolve (typo, expired domain, DNS not propagated)
  | 'website_not_supported' // Firecrawl returned 403 — host is blocklisted; retry is futile
  | 'bad_request' // 4xx, server rejected the URL itself
  | 'already_running' // 409, a build for this website is in flight under another brief
  | 'server_error' // 5xx or background-task internal failure
  | 'network' // browser never reached the backend
  | 'unknown'; // catch-all, same copy as server_error

interface ClassifiedError {
  message: string | null;
  errorClass: AutoOnboardingErrorClass;
  errorCategory: AutoOnboardingErrorCategory;
  httpStatus: number | undefined;
}

function classifyMutationError(error: unknown): ClassifiedError {
  if (error instanceof AxiosError) {
    const backendMessage = (error.response?.data as ApiErrorResponse | undefined)?.errors?.root?.[0]
      ?.message;
    const message =
      typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : error.message || null;
    const status = error.response?.status;
    let errorClass: AutoOnboardingErrorClass;
    let errorCategory: AutoOnboardingErrorCategory;
    if (!error.response) {
      // No response = the request failed before getting a status. Axios sets
      // `message = 'Network Error'` for this. Treat it as `network` regardless
      // of the literal so it survives axios version bumps and i18n.
      errorClass = 'network';
      errorCategory = 'network';
    } else if (status && status >= 500) {
      errorClass = 'http_5xx';
      errorCategory = 'server_error';
    } else if (status === 409) {
      // The backend refused rather than let this submit silently inherit the
      // in-flight run's result, so say so instead of blaming the URL.
      errorClass = 'http_4xx';
      errorCategory = 'already_running';
    } else if (status && status >= 400) {
      errorClass = 'http_4xx';
      errorCategory = 'bad_request';
    } else {
      errorClass = 'unknown';
      errorCategory = 'unknown';
    }
    return { message, errorClass, errorCategory, httpStatus: status };
  }
  const message = error instanceof Error ? error.message || null : null;
  return { message, errorClass: 'unknown', errorCategory: 'unknown', httpStatus: undefined };
}

// The description endpoint dispatches its task before writing a response, so a
// failure that carries no response cannot say whether the build was accepted. A
// browser that was already offline when the POST was dispatched is the one hard
// negative: the request never left. Going offline afterwards proves nothing —
// the response can be lost on a connection that carried the request just fine —
// so the caller passes the connectivity it saw at dispatch, not at callback time.
function isAmbiguousDispatchFailure(error: unknown, wasOnlineAtDispatch: boolean): boolean {
  return wasOnlineAtDispatch && classifyMutationError(error).errorClass === 'network';
}

// Narrows a free-form string from the backend WS payload into a known
// category. Unknown strings (or missing payloads — e.g. older backends) fall
// back to `unknown` so the view layer can render the generic friendly copy.
function parseBackendCategory(raw: string | undefined | null): AutoOnboardingErrorCategory {
  if (
    raw === 'scrape_failed' ||
    raw === 'access_blocked' ||
    raw === 'timeout' ||
    raw === 'dns_failure'
  )
    return raw;
  if (raw === 'website_not_supported') return raw;
  if (raw === 'server_error' || raw === 'bad_request' || raw === 'network') return raw;
  return 'unknown';
}

// Mirrors backend `scrape_rejection_reason` REJECT_* values (firecrawl_client.py); drives the advisory low-quality banner.
export type LowQualityReason =
  | 'http_error'
  | 'soft_404'
  | 'error_page'
  | 'empty_content'
  | 'thin_content'
  | 'off_domain_redirect'
  | 'auth_wall';

function parseWarningReason(raw: string | undefined | null): LowQualityReason | null {
  if (
    raw === 'http_error' ||
    raw === 'soft_404' ||
    raw === 'error_page' ||
    raw === 'empty_content' ||
    raw === 'thin_content' ||
    raw === 'off_domain_redirect' ||
    raw === 'auth_wall'
  ) {
    return raw;
  }
  return null;
}

export type AutoOnboardingStep = {
  step: string;
  // Most fields are strings, but the `completed` event's `summary` is
  // `SummaryItem[]`. Consumers that need that shape cast through `unknown`.
  data?: Record<string, string>;
};

export type AutoOnboardingState = 'idle' | 'processing' | 'completed' | 'error';
export type AutoOnboardingSourceType = 'shopify' | 'generic' | 'description';

type DescriptionOnboardingStartResponse = {
  status: 'started';
  run_id?: string;
};

type DescriptionOnboardingInput = {
  description: string;
  run_id: string;
};

type UrlOnboardingInput = {
  url: string;
  /** Extra context typed alongside the URL. Omitted entirely when absent. */
  description?: string;
};

function createDescriptionRunId(): string {
  return crypto.randomUUID().replace(/-/g, '');
}

// A description build outlives the page it was started from: the tab can reload
// while the worker is still generating. Session-scoped (a fresh tab must not
// inherit someone else's run) and scenario-scoped (switching agents in one tab
// must not either) — hence read per call, since the SPA moves between agents
// without a reload and a module-load-time id would still name the first one.
export const descriptionRunKey = (): string => `__qc_gs_description_run_${getCurrentScenarioId()}`;

// `submitResponseLost` rides along so the terminal event that eventually resolves
// the run still reports it after a reload, not just in the page life that submitted.
type PendingDescriptionRun = { runId: string; startedAt: number; submitResponseLost?: boolean };

/** The run this tab was waiting on when it reloaded, if it can still be alive. */
function readPendingDescriptionRun(): PendingDescriptionRun | null {
  try {
    const raw = sessionStorage.getItem(descriptionRunKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingDescriptionRun>;
    if (typeof parsed?.runId !== 'string' || !parsed.runId) return null;
    if (typeof parsed.startedAt !== 'number' || !Number.isFinite(parsed.startedAt)) return null;
    // Past the give-up window the backend task budget is long gone, so the
    // progress row is the only truth left and the normal derivation owns it.
    if (Date.now() - parsed.startedAt > IDLE_TIMEOUT_MS) return null;
    return {
      runId: parsed.runId,
      startedAt: parsed.startedAt,
      submitResponseLost: parsed.submitResponseLost === true,
    };
  } catch {
    // Unparseable value, or storage denied (Safari private mode) — degrade to
    // the pre-existing behaviour rather than taking the page down.
    return null;
  }
}

function writePendingDescriptionRun(run: PendingDescriptionRun | null): void {
  try {
    if (run) sessionStorage.setItem(descriptionRunKey(), JSON.stringify(run));
    else sessionStorage.removeItem(descriptionRunKey());
  } catch {
    // Storage denied — the run still completes, it just cannot survive a reload.
  }
}

export interface UseAutomaticOnboardingResult {
  state: AutoOnboardingState;
  steps: AutoOnboardingStep[];
  /**
   * The user-facing category for the current failure. Drives which localized
   * string the error view renders. `null` outside the `error` state.
   *
   * Deliberately NOT a raw error message — the backend sends `raw_message`
   * separately for logs/PostHog, but we never render it (PR fixing the
   * Firecrawl-error leak). Previous `errorMessage` field was removed for the
   * same reason — consumers should read `errorCategory` only.
   */
  errorCategory: AutoOnboardingErrorCategory | null;
  /**
   * Advisory low-quality-source reason (maintenance page, login wall, thin
   * content). Set independently of `state` — the flow still completes normally
   * with a neutral persona; this only drives a non-blocking warning banner.
   */
  warningReason: LowQualityReason | null;
  urlType: AutoOnboardingSourceType | null;
  submittedUrl: string | null;
  submitUrl: (url: string, description?: string) => void;
  submitDescription: (description: string, brand: OnboardingBrand) => void;
  isSubmitting: boolean;
  reset: () => void;
}

export function useAutomaticOnboarding(): UseAutomaticOnboardingResult {
  const queryClient = useQueryClient();
  // On fresh mount, consult the cached progress row. If the scrape previously
  // completed (kb_step flag set) and the user hasn't finished the wizard yet,
  // rehydrate into `completed` so the component renders the post-step wizard
  // instead of the URL form. The live WS event stream is gone — the summary
  // card degrades to its minimal "ready" branch via the `steps.length === 0`
  // fallback inside `AutomaticOnboarding`.
  const cachedProgress = queryClient.getQueryData<GettingStartedProgressDto | null>([
    QueryKey.gettingStarted.progress,
  ]);
  const resumed = useRef(isAutomaticResumeable(cachedProgress)).current;
  // A build still running when the tab reloaded. Without this the form comes
  // back enabled mid-generation, and a second submit is rejected behind the
  // first run's lease — burying a completion that was already on its way.
  const pendingRun = useRef(resumed ? null : readPendingDescriptionRun()).current;

  const [state, setState] = useState<AutoOnboardingState>(
    resumed ? 'completed' : pendingRun ? 'processing' : 'idle',
  );
  const [steps, setSteps] = useState<AutoOnboardingStep[]>([]);
  const [errorCategory, setErrorCategory] = useState<AutoOnboardingErrorCategory | null>(null);
  // On refresh-resume the live WS stream (and its low_quality_source event) is
  // gone; rehydrate the advisory from the persisted progress row so a rejected
  // source doesn't silently read as a clean "ready" result.
  const [warningReason, setWarningReason] = useState<LowQualityReason | null>(
    resumed ? parseWarningReason(cachedProgress?.automatic_low_quality_reason) : null,
  );
  const initialSourceType: AutoOnboardingSourceType | null =
    pendingRun !== null || (resumed && cachedProgress?.automatic_source_type === 'description')
      ? 'description'
      : null;
  const [urlType, setUrlType] = useState<AutoOnboardingSourceType | null>(initialSourceType);
  const [submittedUrl, setSubmittedUrl] = useState<string | null>(null);
  const { socket } = useSocket();
  const { refreshConfiguration } = usePreview();
  const { capture } = usePostHogAnalytics();
  const stateRef = useRef(state);
  stateRef.current = state;
  const warningReasonRef = useRef(warningReason);
  warningReasonRef.current = warningReason;
  // Running count of `setting_applied` events since the last submit. Reset in
  // `submitUrl` alongside the other per-run state so a retry starts at zero.
  const settingsAppliedCountRef = useRef(0);
  // Snapshot of the URL + detected type for this run so we can attach them to
  // the terminal completed/failed PostHog events. Socket events don't carry
  // the URL back, and `urlType` is state that may not have flushed by the time
  // `completed` arrives — refs sidestep both problems.
  const submittedUrlRef = useRef<string | null>(null);
  const urlTypeRef = useRef<AutoOnboardingSourceType | null>(initialSourceType);
  // The websocket room is per scenario, so events must match the active source and run.
  const runIdRef = useRef<string | null>(pendingRun?.runId ?? null);
  // A combined run is a URL run by source and carries no `run_id`, so neither of
  // the redaction signals below sees the brief that went into its generation, and
  // a reload mid-build leaves no marker to rebuild it from. "May have" is the
  // honest default: only `submitUrl` knows what was typed, so any event this
  // mount cannot attribute to its own submit is treated as brief-derived.
  const urlRunMayHaveCarriedBriefRef = useRef(true);
  const ignoredRunIdsRef = useRef(new Set<string>());
  const isForAnotherRun = (data: Record<string, string> | undefined): boolean => {
    if (!data?.run_id) return Boolean(runIdRef.current);
    if (ignoredRunIdsRef.current.has(data.run_id)) return true;
    if (submittedUrlRef.current && urlTypeRef.current !== 'description') return true;
    return Boolean(runIdRef.current && data.run_id !== runIdRef.current);
  };

  // Description failures can quote model output derived from the user's private
  // brief (a schema-validation message names what it rejected), so every failure
  // path funnels through here rather than each deciding for itself. The same is
  // true of a URL run submitted with a brief: it reaches the same generator with
  // the brief in its input, and the backend puts the raw exception text on the
  // wire as `raw_message`.
  const analyticsErrorMessage = (
    raw: string,
    category: AutoOnboardingErrorCategory,
    data?: Record<string, string>,
    forceRedact = false,
  ): string =>
    forceRedact ||
    urlTypeRef.current === 'description' ||
    urlRunMayHaveCarriedBriefRef.current ||
    Boolean(data?.run_id)
      ? category
      : raw;
  // Wall-clock of the submit that started the current run, so every terminal
  // event can carry how long the user actually waited. Client-side on purpose:
  // it spans queue time, generation and websocket delivery, which is the number
  // the user experiences and which no backend-only timing can see. Null when the
  // wizard is resumed after the build already finished, so a resumed run reports
  // no duration rather than a fabricated one. A build still in flight across a
  // reload keeps its submit time — that is the wait the user is really sitting
  // through.
  const runStartedAtRef = useRef<number | null>(pendingRun?.startedAt ?? null);
  // Set when a description submit failed with no response at all, so the terminal
  // event that eventually resolves the run says whether the build was accepted.
  // Restored from the persisted run so a reload mid-build keeps reporting it.
  const submitResponseLostRef = useRef(pendingRun?.submitResponseLost === true);
  const elapsedMs = (): number | undefined =>
    runStartedAtRef.current === null ? undefined : Date.now() - runStartedAtRef.current;
  // Idle watchdog timer id, re-armed on each progress event so only real silence trips it.
  const idleWatchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Whether this run already spent its one extra window on a reconciliation that
  // could not reach the server.
  const reconcileRetriedRef = useRef(false);

  // Persist the "scrape done" signal on the progress row so a page refresh
  // drops the user back into the post-step wizard. Uses the shared mutation
  // defaults (see queryClient.ts) so we just feed it a delta. On success we
  // merge only the fields we owned into the cache rather than overwrite with
  // the full server response — another concurrent PATCH (e.g. the
  // deployment-step Finish click) may have updated the row between when this
  // request was dispatched and when the server echoed its view back, and a
  // full overwrite would regress those newer fields.
  const progressMutation = useMutation<
    GettingStartedProgressDto,
    Error,
    Partial<GettingStartedProgressDto>
  >({
    mutationKey: [QueryKey.gettingStarted.progress],
    onSuccess: (_updated, variables) => {
      queryClient.setQueryData<GettingStartedProgressDto | null>(
        [QueryKey.gettingStarted.progress],
        (prev) => (prev ? { ...prev, ...variables } : prev),
      );
    },
  });

  // Terminal "scrape done": refresh caches, enter the post-step wizard, persist the resume flag, and tag PostHog (`recovered` makes silent-stuck recovery measurable).
  const applyCompletion = useCallback(
    (recovered?: 'reconnect' | 'timeout') => {
      // Flip the ref synchronously so a second recovery path (reconnect/watchdog/late event) resolving in the same tick sees `completed` at its guard and bails instead of double-applying before React re-renders.
      stateRef.current = 'completed';
      writePendingDescriptionRun(null);
      // Restore the advisory the confetti gate reads at Finish: recovery
      // refetched the row first, while live completion mirrors the terminal
      // event's verdict (or the earlier low_quality_source event from an older
      // backend) into the cache the page already owns.
      if (recovered) {
        const fresh = queryClient.getQueryData<GettingStartedProgressDto | null>([
          QueryKey.gettingStarted.progress,
        ]);
        setWarningReason((prev) => prev ?? parseWarningReason(fresh?.automatic_low_quality_reason));
      } else {
        queryClient.setQueryData<GettingStartedProgressDto | null>(
          [QueryKey.gettingStarted.progress],
          (prev) =>
            prev ? { ...prev, automatic_low_quality_reason: warningReasonRef.current } : prev,
        );
      }
      queryClient.invalidateQueries({ queryKey: ['knowledge_base'] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.widgetSettings] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.aiAssistants.get] });
      refreshConfiguration();
      setState('completed');
      progressMutation.mutate(
        urlTypeRef.current === 'description'
          ? { identity_step_completed: true }
          : { knowledge_base_step_completed: true },
      );
      // Known-generic only: Shopify never maps (a dead pending flag would poll an idle
      // 404), and a recovered run that missed `url_type_detected` (type null) skips
      // rather than flag blindly. A recovered generic run still opens the review — the
      // backend mapped it regardless of whether the client saw the live event.
      if (urlTypeRef.current === 'generic') {
        localStorage.removeItem(SITEMAP_CONSUMED_KEY);
        setSiteMapPending();
        capture(PostHogEventEnum.SiteScrapeMapStarted, {
          source: 'onboarding_auto',
          url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
        });
      }
      capture(PostHogEventEnum.GettingStartedAutomaticCompleted, {
        url_type: urlTypeRef.current,
        settings_count: settingsAppliedCountRef.current,
        url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
        duration_ms: elapsedMs(),
        ...(recovered ? { recovered } : {}),
        ...(submitResponseLostRef.current ? { submit_response_lost: true } : {}),
      });
    },
    [queryClient, refreshConfiguration, progressMutation.mutate, capture],
  );

  // (Re)arm the idle watchdog. Called when processing begins and on every progress
  // event, so a scrape that keeps streaming is never killed; only IDLE_TIMEOUT_MS of
  // real silence trips it. On fire, reconcile once against the server row — advance if
  // the scrape finished, else surface the recoverable error instead of an infinite spinner.
  const resetIdleWatchdog = useCallback(() => {
    if (stateRef.current !== 'processing') return;
    if (idleWatchdogTimerRef.current !== null) clearTimeout(idleWatchdogTimerRef.current);
    idleWatchdogTimerRef.current = setTimeout(() => {
      if (stateRef.current !== 'processing') return;
      const progressKey = [QueryKey.gettingStarted.progress];
      // `refetchQueries` swallows fetch errors by default and resolves without
      // fetching at all while the browser is offline, so ask it to throw and
      // check connectivity — otherwise a failed poll looks identical to a poll
      // that came back saying "not finished".
      void queryClient
        .refetchQueries({ queryKey: progressKey }, { throwOnError: true })
        .then(() => navigator.onLine !== false)
        .catch(() => false)
        .then((reconciled) => {
          if (stateRef.current !== 'processing') return;
          const fresh = queryClient.getQueryData<GettingStartedProgressDto | null>(progressKey);
          if (hasCurrentRunCompleted(fresh, urlTypeRef.current)) {
            if (fresh?.automatic_source_type === 'description') {
              urlTypeRef.current = 'description';
              setUrlType('description');
            }
            applyCompletion('timeout');
            return;
          }
          // A reconciliation that never reached the server says nothing about the
          // run, so the row it left behind is not proof the backend went silent.
          // Give the build one more window rather than burying a completion that
          // may have landed; bounded to a single extra cycle so a broken network
          // still ends in an error view instead of an endless spinner.
          if (!reconciled && !reconcileRetriedRef.current) {
            reconcileRetriedRef.current = true;
            resetIdleWatchdogRef.current();
            return;
          }
          stateRef.current = 'error';
          setState('error');
          setErrorCategory('timeout');
          writePendingDescriptionRun(null);
          capture(PostHogEventEnum.GettingStartedAutomaticFailed, {
            error_message: 'no_terminal_event',
            error_class: 'backend',
            error_category: 'timeout',
            url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
            duration_ms: elapsedMs(),
            ...(reconciled ? {} : { reconcile_failed: true }),
            ...(submitResponseLostRef.current ? { submit_response_lost: true } : {}),
          });
        });
    }, IDLE_TIMEOUT_MS);
  }, [queryClient, applyCompletion, capture]);
  // The watchdog re-arms itself after an unreconciled fire; a ref keeps that
  // self-reference out of its own dependency list.
  const resetIdleWatchdogRef = useRef(resetIdleWatchdog);
  resetIdleWatchdogRef.current = resetIdleWatchdog;

  // Listen for websocket progress events
  useEffect(() => {
    const applyEvent = (event: { step: string; data?: Record<string, string> }) => {
      if (isForAnotherRun(event.data)) return;
      // Accept events only when we've kicked the backend job off (idle right
      // after submit, processing while it runs). Once we've reached `completed`
      // or `error`, late events from a retry of a previous run must be ignored
      // so they don't overwrite the current state machine.
      const isAcceptingEvents = stateRef.current === 'processing' || stateRef.current === 'idle';
      if (!isAcceptingEvents) {
        // Still ignored by the state machine, but `ignored_in_state` keeps it out of the failure funnel.
        if (event.step === 'error') {
          capture(PostHogEventEnum.GettingStartedAutomaticFailed, {
            error_message: analyticsErrorMessage(
              event.data?.raw_message ?? event.data?.message ?? '',
              parseBackendCategory(event.data?.category),
              event.data,
            ),
            error_class: 'backend',
            error_category: parseBackendCategory(event.data?.category),
            ignored_in_state: stateRef.current,
            url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
            // How late the backend spoke relative to the submit — the number
            // that tells us whether IDLE_TIMEOUT_MS is set too tight.
            duration_ms: elapsedMs(),
          });
        }
        return;
      }

      if (event.step === 'started') {
        // First sighting of this run's id: everything terminal is matched against it.
        if (event.data?.run_id && !runIdRef.current) runIdRef.current = event.data.run_id;
        setState('processing');
      }

      if (event.step === 'url_type_detected' && event.data?.url_type) {
        const detected = event.data.url_type as 'shopify' | 'generic';
        setUrlType(detected);
        urlTypeRef.current = detected;
      }

      if (event.step === 'setting_applied') {
        settingsAppliedCountRef.current += 1;
        // Refresh sidebar when agent name or avatar changes
        if (event.data?.setting === 'ai_agent_name' || event.data?.setting === 'avatar') {
          queryClient.invalidateQueries({ queryKey: [QueryKey.aiAssistants.get] });
        }
      }

      // Non-terminal: the backend still finishes with a neutral persona, so we only
      // raise an advisory banner and let the stream continue to `completed`.
      if (event.step === 'low_quality_source') {
        const parsedReason = parseWarningReason(event.data?.reason);
        // Sync the ref in the same tick: socket.io can deliver this event and
        // `completed` in one batch, and applyCompletion reads the ref before
        // React re-renders the state mirror.
        warningReasonRef.current = parsedReason;
        setWarningReason(parsedReason);
        capture(PostHogEventEnum.GettingStartedAutomaticLowQuality, {
          reason: event.data?.reason ?? '',
          url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
        });
      }

      if (event.step === 'error') {
        stateRef.current = 'error';
        setState('error');
        writePendingDescriptionRun(null);
        // Backend sends `{category, raw_message}` — category drives the
        // localized copy, raw_message stays out of the UI entirely. Older
        // backends that sent `{message}` fall through to `unknown`; that's
        // fine since the copy is the same friendly fallback.
        const category = parseBackendCategory(event.data?.category);
        setErrorCategory(category);
        // `error_class: 'backend'` distinguishes a WS failure (request reached
        // the server, the background task failed) from the mutation onError
        // paths below (`network` / `http_*`). Same event name; the property
        // tells you where in the stack it died. `error_message` carries the
        // raw backend string for our own debugging in PostHog — never rendered.
        const rawMessage = event.data?.raw_message ?? event.data?.message ?? '';
        capture(PostHogEventEnum.GettingStartedAutomaticFailed, {
          // A description failure can carry model output derived from the user's
          // private brief (a schema-validation message quotes what it rejected),
          // so only the bounded category leaves the client. The raw text is
          // already in the backend logs for debugging.
          error_message: analyticsErrorMessage(rawMessage, category, event.data),
          error_class: 'backend',
          error_category: category,
          url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
          duration_ms: elapsedMs(),
          ...(submitResponseLostRef.current ? { submit_response_lost: true } : {}),
        });
        return;
      }

      if (event.step === 'completed') {
        // New backends repeat the verdict on the terminal event. This closes
        // the reconnect window where `low_quality_source` was missed but the
        // later `completed` event arrived. Preserve the ref when the field is
        // absent so rolling deploys with an older backend keep using the
        // earlier event.
        if (event.data?.low_quality_reason !== undefined) {
          const completedReason = parseWarningReason(event.data.low_quality_reason);
          warningReasonRef.current = completedReason;
          setWarningReason(completedReason);
        }
        // The terminal payload carries the authoritative url_type; adopt it before gating the site-map review, since a reconnect may have missed the earlier `url_type_detected`.
        const completedType = event.data?.url_type;
        if (
          completedType === 'generic' ||
          completedType === 'shopify' ||
          completedType === 'description'
        ) {
          urlTypeRef.current = completedType;
          setUrlType(completedType);
        }
        applyCompletion();
      }

      setSteps((prev) => [...prev, { step: event.step, data: event.data }]);
      // Every non-terminal event is a sign of life — re-arm the idle watchdog. `error`
      // returns early above and `completed` has already flipped `stateRef`, so both are
      // no-ops here (the guard inside `resetIdleWatchdog` bails when not processing).
      resetIdleWatchdog();
    };

    // A socket callback runs outside React, so a throw here reaches no error
    // boundary: recover in place or the progress card hangs until the watchdog.
    const handler = (event: { step: string; data?: Record<string, string> }) => {
      try {
        applyEvent(event);
      } catch (error) {
        const payloadChars = Object.values(event.data ?? {}).reduce(
          (total, value) => total + String(value).length,
          0,
        );
        // eslint-disable-next-line no-console
        console.error(
          `Getting Started: failed handling "${event.step}" (setting=${event.data?.setting ?? 'none'}, payload_chars=${payloadChars})`,
          error,
        );
        capture(PostHogEventEnum.GettingStartedAutomaticFailed, {
          // Same redaction as every other capture site: a description run's payload
          // is derived from the user's private brief.
          error_message: analyticsErrorMessage(
            error instanceof Error ? error.message : 'handler_threw',
            'server_error',
            event.data,
          ),
          error_class: 'client',
          error_category: 'server_error',
          failed_step: event.step,
          failed_setting: event.data?.setting,
          payload_chars: payloadChars,
          url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
          duration_ms: elapsedMs(),
        });
        // A crash on a late event must not undo a run that already finished.
        if (stateRef.current === 'processing' || stateRef.current === 'idle') {
          stateRef.current = 'error';
          setState('error');
          // Our own code broke, so "there's a problem on our end" is the honest
          // copy; `unknown` would tell the user to go check their URL.
          setErrorCategory('server_error');
          writePendingDescriptionRun(null);
        }
      }
    };

    socket.on(SocketKey.onboarding.fromUrlProgress, handler);
    return () => {
      socket.off(SocketKey.onboarding.fromUrlProgress, handler);
    };
  }, [socket, queryClient, capture, applyCompletion, resetIdleWatchdog]);

  // A reconnected socket can miss the one-shot `completed` event; on (re)connect while processing, reconcile against the server progress row (backend persists the flag on success) and advance if the scrape finished while we were away.
  useEffect(() => {
    const onConnect = () => {
      if (stateRef.current !== 'processing') return;
      void queryClient.refetchQueries({ queryKey: [QueryKey.gettingStarted.progress] }).then(() => {
        if (stateRef.current !== 'processing') return;
        const fresh = queryClient.getQueryData<GettingStartedProgressDto | null>([
          QueryKey.gettingStarted.progress,
        ]);
        if (hasCurrentRunCompleted(fresh, urlTypeRef.current)) {
          if (fresh?.automatic_source_type === 'description') {
            urlTypeRef.current = 'description';
            setUrlType('description');
          }
          applyCompletion('reconnect');
        }
      });
    };
    socket.on('connect', onConnect);
    return () => {
      socket.off('connect', onConnect);
    };
  }, [socket, queryClient, applyCompletion]);

  // Arm the idle watchdog when processing begins; each progress event re-arms it (see the
  // handler above), and leaving `processing` (or unmount) clears it.
  useEffect(() => {
    if (state !== 'processing') return;
    resetIdleWatchdog();
    return () => {
      if (idleWatchdogTimerRef.current !== null) {
        clearTimeout(idleWatchdogTimerRef.current);
        idleWatchdogTimerRef.current = null;
      }
    };
  }, [state, resetIdleWatchdog]);

  const handleMutationError = useCallback(
    (error: unknown, forceRedact = false) => {
      const {
        message,
        errorClass: cls,
        errorCategory: cat,
        httpStatus,
      } = classifyMutationError(error);
      stateRef.current = 'error';
      setState('error');
      setErrorCategory(cat);
      writePendingDescriptionRun(null);
      capture(PostHogEventEnum.GettingStartedAutomaticFailed, {
        error_message: analyticsErrorMessage(message ?? '', cat, undefined, forceRedact),
        error_class: cls,
        error_category: cat,
        http_status: httpStatus,
        url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
        duration_ms: elapsedMs(),
      });
    },
    [capture],
  );

  const urlSubmissionIdRef = useRef(0);
  const urlMutation = useMutation<unknown, Error, UrlOnboardingInput>({
    mutationKey: [QueryKey.onboarding.fromUrl],
    // `submitUrl` flips state to `processing` synchronously before the mutation
    // fires, so a POST failure (rate-limit 429, 400 invalid URL, 5xx) would
    // otherwise strand the UI on the progress card forever — no WS events
    // arrive because the backend job was never dispatched. Mirror the WS
    // `error` handler: transition to `error`, surface the backend message
    // (falling back to null so the view can localize), and fire the same
    // PostHog failure event. The `isAcceptingEvents` guard in the WS handler
    // then drops any stale events from a prior run.
  });

  const descriptionMutation = useMutation<
    DescriptionOnboardingStartResponse,
    Error,
    DescriptionOnboardingInput
  >({
    mutationKey: [QueryKey.onboarding.fromDescription],
  });

  const submitUrl = useCallback(
    // A URL-bearing submit routes here even when a description rides along: this
    // path nulls `runIdRef` and sets `submittedUrlRef`, and the URL flow emits no
    // `run_id`. Giving a combined run one would make `isForAnotherRun` drop every
    // event it receives.
    (url: string, description?: string) => {
      const cleanedDescription = description?.trim() ?? '';
      stateRef.current = 'processing';
      setState('processing');
      setSteps([]);
      setErrorCategory(null);
      setWarningReason(null);
      setUrlType(null);
      setSubmittedUrl(url);
      settingsAppliedCountRef.current = 0;
      submittedUrlRef.current = url;
      urlTypeRef.current = null;
      urlRunMayHaveCarriedBriefRef.current = cleanedDescription !== '';
      if (runIdRef.current) ignoredRunIdsRef.current.add(runIdRef.current);
      runIdRef.current = null;
      runStartedAtRef.current = Date.now();
      submitResponseLostRef.current = false;
      reconcileRetriedRef.current = false;
      writePendingDescriptionRun(null);
      const submissionId = ++urlSubmissionIdRef.current;
      // Commit the user's mode choice to the DB. The UI normalizes `choice`
      // to `automatic` so this looks identical client-side, but production
      // funnel queries that filter by `mode='automatic'` were silently
      // missing every automatic-flow user (their DB row stayed `choice`).
      progressMutation.mutate({ mode: 'automatic' });
      // Deliberately NOT pre-registering the URL in the upload store: the
      // bottom-right scrape progress widget would compete for attention with
      // the onboarding flow itself. Once the user clicks through onboarding
      // and lands on the KB page, the page-header bar and sidebar warning
      // icon (driven by useKbUsageSocket → /allowed_features) surface the
      // KB-limit state if the post-onboarding sitewide crawl saturates the
      // plan limit — a quieter, contextual signal than a transient pill.
      capture(PostHogEventEnum.GettingStartedAutomaticSubmitted, {
        url_host: extractUrlHost(url),
        // Length only: the brief is the user's own words and is excluded from
        // analytics on the description path for the same reason.
        description_length: descriptionLength(cleanedDescription),
      });
      urlMutation.mutate(
        // Spread rather than always sending `description: undefined`, so a
        // URL-only submit posts exactly the body it posts today.
        { url, ...(cleanedDescription ? { description: cleanedDescription } : {}) },
        {
          onError: (error) => {
            if (submissionId !== urlSubmissionIdRef.current || stateRef.current !== 'processing')
              return;
            handleMutationError(error);
          },
        },
      );
    },
    [urlMutation, progressMutation, capture, handleMutationError],
  );

  const submitDescription = useCallback(
    (description: string, brand: OnboardingBrand) => {
      const cleanedDescription = description.trim();
      const runId = createDescriptionRunId();
      stateRef.current = 'processing';
      setState('processing');
      setSteps([]);
      setErrorCategory(null);
      setWarningReason(null);
      setUrlType('description');
      setSubmittedUrl(null);
      settingsAppliedCountRef.current = 0;
      submittedUrlRef.current = null;
      urlTypeRef.current = 'description';
      if (runIdRef.current) ignoredRunIdsRef.current.add(runIdRef.current);
      runIdRef.current = runId;
      const startedAt = Date.now();
      runStartedAtRef.current = startedAt;
      submitResponseLostRef.current = false;
      reconcileRetriedRef.current = false;
      // Before the request leaves, so a reload during the POST still resumes.
      writePendingDescriptionRun({ runId, startedAt });
      urlSubmissionIdRef.current += 1;
      progressMutation.mutate({ mode: 'automatic' });
      capture(PostHogEventEnum.GettingStartedDescriptionSubmitted, {
        brand,
        description_length: descriptionLength(cleanedDescription),
      });
      if (brand === 'discord') {
        capture(PostHogEventEnum.GettingStartedDiscordDescriptionSubmitted, {
          description_length: descriptionLength(cleanedDescription),
        });
      }
      // Read now, not in `onError`: only connectivity at dispatch can prove the
      // request never left.
      const wasOnlineAtDispatch = navigator.onLine !== false;
      descriptionMutation.mutate(
        { description: cleanedDescription, run_id: runId },
        {
          onSuccess: (response) => {
            if (runIdRef.current !== runId || stateRef.current !== 'processing') return;
            runIdRef.current = response.run_id ?? null;
            // A backend that echoed no id leaves nothing to rebind to on reload.
            writePendingDescriptionRun(
              response.run_id ? { runId: response.run_id, startedAt } : null,
            );
          },
          onError: (error) => {
            if (runIdRef.current !== runId || stateRef.current !== 'processing') return;
            // An unresolved dispatch may sit over a build that was accepted, so let
            // this run's terminal event or the watchdog's progress-row reconciliation
            // decide instead of failing it here. The run id is the browser's own, so
            // the live events and a reload both still bind to it.
            if (isAmbiguousDispatchFailure(error, wasOnlineAtDispatch)) {
              submitResponseLostRef.current = true;
              writePendingDescriptionRun({ runId, startedAt, submitResponseLost: true });
              return;
            }
            handleMutationError(error, true);
          },
        },
      );
    },
    [descriptionMutation, progressMutation, capture, handleMutationError],
  );

  const reset = useCallback(() => {
    stateRef.current = 'idle';
    setState('idle');
    setSteps([]);
    setErrorCategory(null);
    warningReasonRef.current = null;
    setWarningReason(null);
    setUrlType(null);
    urlTypeRef.current = null;
    setSubmittedUrl(null);
    submittedUrlRef.current = null;
    // Back to unknown: a late event from the abandoned run is no longer this
    // mount's to classify, and its raw text was already captured when the run
    // first reported terminal.
    urlRunMayHaveCarriedBriefRef.current = true;
    if (runIdRef.current) ignoredRunIdsRef.current.add(runIdRef.current);
    runIdRef.current = null;
    runStartedAtRef.current = null;
    submitResponseLostRef.current = false;
    reconcileRetriedRef.current = false;
    writePendingDescriptionRun(null);
    urlSubmissionIdRef.current += 1;
  }, []);

  return {
    state,
    steps,
    errorCategory,
    warningReason,
    urlType,
    submittedUrl,
    submitUrl,
    submitDescription,
    isSubmitting: urlMutation.isPending || descriptionMutation.isPending,
    reset,
  };
}
